import { LitElement, html, css } from 'lit';

// Helper to escape regex special chars
function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export class UIAutocomplete extends LitElement {
  static formAssociated = true;

  static properties = {
    name: { type: String },
    value: { type: String, reflect: true },
    placeholder: { type: String },
    disabled: { type: Boolean, reflect: true },
    required: { type: Boolean, reflect: true },
    options: { type: Array }, // Array of strings or { value, label }
    strict: { type: Boolean }, // If true, only options can be selected
    label: { type: String, reflect: true },
    
    // Internal States
    isOpen: { type: Boolean, state: true },
    inputValue: { type: String, state: true },
    highlightedIndex: { type: Number, state: true }
  };

  static styles = css`
    :host {
      display: inline-block;
      width: 100%;
      position: relative;
      font-family: var(--ui-font-family);
      font-size: var(--ui-font-size-base);
      color: var(--ui-text);
    }

    .ui-label {
      display: block;
      margin-bottom: 6px;
      font-size: var(--ui-font-size-sm);
      font-weight: 500;
      color: var(--ui-text-muted);
      text-align: left;
    }

    .autocomplete-container {
      position: relative;
      width: 100%;
    }

    .autocomplete-header {
      position: relative;
      display: flex;
      align-items: center;
      width: 100%;
    }

    .autocomplete-input {
      width: 100%;
      height: var(--ui-input-height);
      padding: 0 var(--ui-padding-x);
      padding-right: 40px;
      background-color: var(--ui-surface);
      border: 1px solid var(--ui-border);
      border-radius: var(--ui-radius-md);
      font-family: inherit;
      font-size: inherit;
      color: inherit;
      box-shadow: var(--ui-shadow-sm);
      transition: var(--ui-transition-fast);
      outline: none;
      box-sizing: border-box;
    }

    .autocomplete-input:hover:not(:disabled) {
      border-color: var(--ui-border-hover);
      background-color: var(--ui-surface-hover);
    }

    .autocomplete-input:focus:not(:disabled) {
      border-color: var(--ui-border-focus);
      background-color: var(--ui-surface);
      box-shadow: 0 0 0 4px var(--ui-focus-ring);
    }

    .autocomplete-input:disabled {
      cursor: not-allowed;
      opacity: 0.6;
      background-color: var(--ui-surface-active);
    }

    /* Clear / Dropdown icon controls */
    .autocomplete-controls {
      position: absolute;
      right: var(--ui-padding-x);
      top: 50%;
      transform: translateY(-50%);
      display: flex;
      align-items: center;
      gap: 6px;
      color: var(--ui-text-muted);
    }

    .control-btn {
      border: none;
      background: transparent;
      padding: 0;
      color: inherit;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: var(--ui-transition-fast);
    }

    .control-btn:hover {
      color: var(--ui-text);
    }

    /* Popover panel */
    .autocomplete-panel {
      position: absolute;
      top: calc(100% + 6px);
      left: 0;
      width: 100%;
      max-height: 240px;
      overflow-y: auto;
      background-color: color-mix(in srgb, var(--ui-surface) 90%, transparent);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      border: 1px solid var(--ui-border);
      border-radius: var(--ui-radius-lg);
      box-shadow: var(--ui-shadow-popover);
      z-index: var(--ui-z-popover);
      transform-origin: top;
      opacity: 0;
      transform: scale(0.95);
      pointer-events: none;
      transition: opacity var(--ui-transition-fast), transform 300ms cubic-bezier(0.34, 1.56, 0.64, 1);
      box-sizing: border-box;
      padding: 6px;
    }

    .autocomplete-panel.open {
      opacity: 1;
      transform: scale(1);
      pointer-events: auto;
    }

    /* List options styling */
    .autocomplete-option {
      padding: 10px var(--ui-padding-x);
      border-radius: var(--ui-radius-sm);
      cursor: pointer;
      font-size: var(--ui-font-size-base);
      transition: var(--ui-transition-fast);
      display: flex;
      align-items: center;
      justify-content: space-between;
      color: var(--ui-text);
    }

    .autocomplete-option:hover {
      background-color: var(--ui-surface-hover);
    }

    .autocomplete-option.highlighted {
      background-color: var(--ui-surface-active);
      color: var(--ui-primary);
    }

    .autocomplete-option.selected {
      background-color: var(--ui-primary);
      color: var(--ui-text-on-primary);
      font-weight: 600;
    }

    /* Text highlight match */
    .highlight {
      font-weight: 700;
      color: var(--ui-primary);
      text-decoration: underline;
    }

    .autocomplete-option.selected .highlight {
      color: inherit;
      text-decoration: underline;
    }

    .empty-state {
      padding: 12px;
      text-align: center;
      color: var(--ui-text-muted);
      font-size: var(--ui-font-size-sm);
    }

    /* Mobile sheet styling */
    .backdrop {
      display: none;
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background-color: rgba(0, 0, 0, 0.4);
      backdrop-filter: blur(4px);
      z-index: calc(var(--ui-z-popover) - 1);
    }

    @media (max-width: 576px) {
      .autocomplete-container.open .backdrop {
        display: block;
      }

      .autocomplete-panel {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        top: auto;
        width: 100%;
        max-width: 100%;
        border-radius: var(--ui-radius-lg) var(--ui-radius-lg) 0 0;
        box-shadow: 0 -10px 25px rgba(0,0,0,0.15);
        transform: translateY(100%);
        transform-origin: bottom;
        transition: transform var(--ui-transition-normal) cubic-bezier(0.25, 0.8, 0.25, 1), opacity var(--ui-transition-normal);
        z-index: var(--ui-z-popover);
      }

      .autocomplete-container.open .autocomplete-panel {
        transform: translateY(0);
        opacity: 1;
      }

      .autocomplete-panel::before {
        content: '';
        display: block;
        width: 40px;
        height: 4px;
        background-color: var(--ui-border);
        border-radius: var(--ui-radius-full);
        margin: -4px auto 12px auto;
      }
    }
  `;

  constructor() {
    super();
    this.internals = this.attachInternals();
    this.name = '';
    this.value = '';
    this.placeholder = 'Rechercher ou saisir...';
    this.disabled = false;
    this.required = false;
    this.options = [];
    this.strict = false;
    this.label = '';

    this.isOpen = false;
    this.inputValue = '';
    this.highlightedIndex = -1;

    this._handleOutsideClick = this._handleOutsideClick.bind(this);
  }

  willUpdate(changedProperties) {
    if (typeof this.options === 'string') {
      try {
        this.options = JSON.parse(this.options);
      } catch (e) {
        this.options = [];
      }
    }

    if (changedProperties.has('value') || changedProperties.has('options')) {
      this._syncInputText();
    }
  }

  get validity() { return this.internals.validity; }
  get validationMessage() { return this.internals.validationMessage; }
  get willValidate() { return this.internals.willValidate; }
  checkValidity() { return this.internals.checkValidity(); }
  reportValidity() { return this.internals.reportValidity(); }

  updated(changedProperties) {
    if (changedProperties.has('value') || changedProperties.has('required')) {
      this.internals.setFormValue(this.value);
      this._updateValidity();
    }
  }

  _updateValidity() {
    const hasValue = this.value && this.value.trim() !== '';
    if (this.required && !hasValue) {
      this.internals.setValidity(
        { valueMissing: true }, 
        'Veuillez sélectionner ou saisir une valeur.',
        this.shadowRoot.querySelector('.autocomplete-input')
      );
    } else {
      this.internals.setValidity({});
    }
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('mousedown', this._handleOutsideClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('mousedown', this._handleOutsideClick);
  }

  _syncInputText() {
    if (this.value) {
      const match = this._findOptionByValue(this.value);
      if (match) {
        this.inputValue = this._getOptionLabel(match);
      } else {
        this.inputValue = this.strict ? '' : this.value;
      }
    } else {
      this.inputValue = '';
    }
  }

  _findOptionByValue(val) {
    return this.options.find(opt => {
      if (typeof opt === 'object' && opt !== null) {
        return String(opt.value) === String(val);
      }
      return String(opt) === String(val);
    });
  }

  _getOptionLabel(opt) {
    if (typeof opt === 'object' && opt !== null) {
      return opt.label || String(opt.value);
    }
    return String(opt);
  }

  _getOptionValue(opt) {
    if (typeof opt === 'object' && opt !== null) {
      return opt.value;
    }
    return String(opt);
  }

  _handleOutsideClick(e) {
    if (this.isOpen && !this.contains(e.target) && !e.composedPath().includes(this)) {
      this._closePanel();
      this._handleBlurValidation();
    }
  }

  _openPanel() {
    if (this.disabled) return;
    this.isOpen = true;
    this.highlightedIndex = -1;
  }

  _closePanel() {
    this.isOpen = false;
    this.highlightedIndex = -1;
  }

  _handleInput(e) {
    const text = e.target.value;
    this.inputValue = text;
    this._openPanel();

    // Trigger custom query event for async data fetching
    this.dispatchEvent(new CustomEvent('query', {
      detail: { query: text },
      bubbles: true,
      composed: true
    }));

    if (!this.strict) {
      this.value = text;
      this._dispatchChangeEvent(text);
    }
  }

  _handleBlurValidation() {
    if (this.strict) {
      // Must match one of the options, otherwise reset to previous value
      const match = this.options.find(opt => this._getOptionLabel(opt).toLowerCase() === this.inputValue.toLowerCase());
      if (match) {
        const val = this._getOptionValue(match);
        this.value = val;
        this.inputValue = this._getOptionLabel(match);
        this._dispatchChangeEvent(val);
      } else {
        // Clear or restore
        this.value = '';
        this.inputValue = '';
        this._dispatchChangeEvent('');
      }
    } else {
      this.value = this.inputValue;
      this._dispatchChangeEvent(this.inputValue);
    }
  }

  _selectOption(opt) {
    const val = this._getOptionValue(opt);
    const label = this._getOptionLabel(opt);
    this.value = val;
    this.inputValue = label;
    this._closePanel();
    this._dispatchChangeEvent(val);
  }

  _clearSelection(e) {
    e.stopPropagation();
    if (this.disabled) return;
    this.value = '';
    this.inputValue = '';
    this._closePanel();
    this._dispatchChangeEvent('');
  }

  _dispatchChangeEvent(val) {
    this.dispatchEvent(new CustomEvent('change', {
      detail: { value: val },
      bubbles: true,
      composed: true
    }));
  }

  _getFilteredOptions() {
    if (!Array.isArray(this.options)) return [];
    if (!this.inputValue) return this.options;
    const query = this.inputValue.toLowerCase();
    return this.options.filter(opt => {
      const label = this._getOptionLabel(opt).toLowerCase();
      return label.includes(query);
    });
  }

  // Safe highlighted HTML renderer
  _renderOptionLabelWithHighlight(opt) {
    const label = this._getOptionLabel(opt);
    if (!this.inputValue) return html`${label}`;
    
    const query = this.inputValue.trim();
    if (!query) return html`${label}`;

    try {
      const regex = new RegExp(`(${escapeRegExp(query)})`, 'gi');
      const parts = label.split(regex);
      return html`
        ${parts.map(part => 
          regex.test(part) ? html`<span class="highlight">${part}</span>` : html`${part}`
        )}
      `;
    } catch (e) {
      return html`${label}`;
    }
  }

  // Keyboard navigation listeners
  _handleKeyDown(e) {
    if (this.disabled) return;

    const filtered = this._getFilteredOptions();

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!this.isOpen) {
        this._openPanel();
      } else {
        this.highlightedIndex = (this.highlightedIndex + 1) % filtered.length;
        this._scrollToOption(this.highlightedIndex);
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!this.isOpen) {
        this._openPanel();
      } else {
        this.highlightedIndex = (this.highlightedIndex - 1 + filtered.length) % filtered.length;
        this._scrollToOption(this.highlightedIndex);
      }
    } else if (e.key === 'Enter') {
      if (this.isOpen) {
        if (this.highlightedIndex >= 0 && this.highlightedIndex < filtered.length) {
          this._selectOption(filtered[this.highlightedIndex]);
          e.preventDefault();
        } else if (filtered.length > 0) {
          // Select first
          this._selectOption(filtered[0]);
          e.preventDefault();
        }
      }
    } else if (e.key === 'Escape') {
      if (this.isOpen) {
        this._closePanel();
        e.preventDefault();
      }
    }
  }

  _scrollToOption(index) {
    setTimeout(() => {
      const panel = this.shadowRoot.querySelector('.autocomplete-panel');
      const optionEl = panel?.querySelectorAll('.autocomplete-option')[index];
      if (optionEl) {
        const panelTop = panel.scrollTop;
        const panelBottom = panelTop + panel.clientHeight;
        const elTop = optionEl.offsetTop;
        const elBottom = elTop + optionEl.clientHeight;
        
        if (elTop < panelTop) {
          panel.scrollTop = elTop;
        } else if (elBottom > panelBottom) {
          panel.scrollTop = elBottom - panel.clientHeight;
        }
      }
    }, 0);
  }

  render() {
    const filtered = this._getFilteredOptions();
    
    return html`
      ${this.label ? html`<label class="ui-label" part="label">${this.label}</label>` : ''}
      <div class="autocomplete-container ${this.isOpen ? 'open' : ''}">
        <div class="autocomplete-header">
          <input
            type="text"
            class="autocomplete-input" part="input"
            .value="${this.inputValue}"
            placeholder="${this.placeholder}"
            ?disabled="${this.disabled}"
            @input="${this._handleInput}"
            @focus="${this._openPanel}"
            @keydown="${this._handleKeyDown}"
          />
          
          <div class="autocomplete-controls">
            ${this.inputValue && !this.disabled ? html`
              <button type="button" class="control-btn" @click="${this._clearSelection}" title="Effacer">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            ` : ''}
            
            <div class="control-btn" style="pointer-events: none;">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            </div>
          </div>
        </div>

        <div class="backdrop" @click="${this._closePanel}"></div>

        <div class="autocomplete-panel ${this.isOpen ? 'open' : ''}">
          ${filtered.length > 0 ? filtered.map((opt, idx) => {
            const isSelected = String(this._getOptionValue(opt)) === String(this.value);
            const isHighlighted = idx === this.highlightedIndex;
            return html`
              <div 
                class="autocomplete-option ${isSelected ? 'selected' : ''} ${isHighlighted ? 'highlighted' : ''}"
                @click="${() => this._selectOption(opt)}"
              >
                <span>${this._renderOptionLabelWithHighlight(opt)}</span>
                ${isSelected ? html`
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                ` : ''}
              </div>
            `;
          }) : html`
            <div class="empty-state">Aucun résultat trouvé</div>
          `}
        </div>
      </div>
    `;
  }
}

if (!customElements.get('ui-autocomplete')) {
  customElements.define('ui-autocomplete', UIAutocomplete);
}
